import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, Mail, Clock, CheckCircle2, ShoppingCart, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

const CONSENT_VERSION = "v1.0";
interface AuthScreenProps {
  onContinueAsGuest: () => void;
  onAuthSuccess: () => void;
  onBack?: () => void;
}

// Email validation schema
const emailSchema = z.string().email("Please enter a valid email address");
export const AuthScreen = ({
  onContinueAsGuest,
  onAuthSuccess,
  onBack
}: AuthScreenProps) => {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Consent state
  const [hasConsented, setHasConsented] = useState(false);
  const [showConsentNudge, setShowConsentNudge] = useState(false);

  // Cooldown timer state
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const {
    signInWithGoogle,
    signInAnonymously,
    signInWithMagicLink,
    user
  } = useAuth();

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [cooldownSeconds]);

  // Auto-hide consent nudge after 4 seconds
  useEffect(() => {
    if (showConsentNudge) {
      const timer = setTimeout(() => {
        setShowConsentNudge(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showConsentNudge]);

  // Start 60-second cooldown
  const startCooldown = useCallback(() => {
    setCooldownSeconds(60);
    setCanResend(false);
  }, []);

  // Show consent nudge popup
  const showConsentPopup = () => {
    setShowConsentNudge(true);
  };

  // Validate email
  const validateEmail = (emailInput: string): boolean => {
    try {
      emailSchema.parse(emailInput);
      return true;
    } catch {
      return false;
    }
  };
  const isEmailValid = validateEmail(email);
  // Log consent for authenticated user
  const logConsent = async (userId: string) => {
    try {
      await supabase.from('consent_logs').insert({
        user_id: userId,
        consent_type: 'health_data_processing',
        consent_version: CONSENT_VERSION,
        user_agent: navigator.userAgent,
      });
    } catch (err) {
      console.error('Consent logging error:', err);
    }
  };

  const handleGoogleLogin = async () => {
    if (!hasConsented) {
      showConsentPopup();
      return;
    }
    setIsLoading("google");
    try {
      const {
        error
      } = await signInWithGoogle();
      if (error) {
        const errorMessage = error.message?.toLowerCase() || "";
        if (errorMessage.includes("403") || errorMessage.includes("access_denied") || errorMessage.includes("forbidden")) {
          toast({
            title: "Access Denied",
            description: "Please ensure your email is added as a Test User in the Google Cloud Console.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Login Failed",
            description: error.message,
            variant: "destructive"
          });
        }
        setIsLoading(null);
        return;
      }
    } catch (err: any) {
      const errorMessage = err?.message?.toLowerCase() || "";
      if (errorMessage.includes("403") || errorMessage.includes("access_denied") || errorMessage.includes("forbidden")) {
        toast({
          title: "Access Denied",
          description: "Please ensure your email is added as a Test User in the Google Cloud Console.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Login Failed",
          description: "An unexpected error occurred.",
          variant: "destructive"
        });
      }
      setIsLoading(null);
    }
  };
  const handleGuestLogin = async () => {
    if (!hasConsented) {
      showConsentPopup();
      return;
    }
    setIsLoading("guest");
    
    // CRITICAL: Clear all local storage to ensure fresh guest session
    // This prevents stale data from previous authenticated sessions
    localStorage.removeItem("metabolic_dietary");
    localStorage.removeItem("metabolic_goals");
    localStorage.removeItem("metabolic_custom_goal");
    localStorage.removeItem("metabolic_list");
    localStorage.removeItem("metabolic_onboarding");
    
    try {
      const {
        error
      } = await signInAnonymously();
      if (error) {
        toast({
          title: "Guest Login Failed",
          description: error.message,
          variant: "destructive"
        });
        setIsLoading(null);
        return;
      }
      // Don't show toast to avoid blocking the dietary preferences page
      onContinueAsGuest();
    } catch (err) {
      toast({
        title: "Guest Login Failed",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
      setIsLoading(null);
    }
  };
  const handleEmailSubmit = async () => {
    if (!hasConsented) {
      showConsentPopup();
      return;
    }
    if (!isEmailValid) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading("email");
    try {
      const {
        error
      } = await signInWithMagicLink(email);
      if (error) {
        toast({
          title: "Failed to Send Link",
          description: error.message,
          variant: "destructive"
        });
        setIsLoading(null);
        return;
      }

      // Success - show confirmation and start cooldown
      setEmailSent(true);
      startCooldown();
      toast({
        title: "Check your inbox!",
        description: `We sent a login link to ${email}`
      });
      setIsLoading(null);
    } catch (err: any) {
      toast({
        title: "Failed to Send Link",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
      setIsLoading(null);
    }
  };

  const handleEmailButtonClick = () => {
    if (!hasConsented) {
      showConsentPopup();
      return;
    }
    setShowEmailInput(true);
  };

  const handleResendLink = async () => {
    if (!canResend) return;
    await handleEmailSubmit();
  };
  const handleBackFromEmail = () => {
    setShowEmailInput(false);
    setEmail("");
    setEmailSent(false);
    setCooldownSeconds(0);
    setCanResend(true);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-[#D8F3DC] z-50 flex flex-col overflow-y-auto">
      {(onBack || showEmailInput) && <motion.button initial={{
      opacity: 0,
      x: -10
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.2
    }} onClick={showEmailInput ? handleBackFromEmail : onBack} className="absolute top-6 left-6 w-10 h-10 rounded-xl bg-[#B7E4C7] flex items-center justify-center hover:bg-[#95D5B2] transition-colors z-10">
          <ArrowLeft className="w-5 h-5 text-[#1B4332]" />
        </motion.button>}

      {/* Consent Nudge Popup */}
      <AnimatePresence>
        {showConsentNudge && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="fixed top-20 left-4 right-4 z-50 mx-auto max-w-sm"
          >
            <div className="bg-white rounded-2xl shadow-xl border border-[#52B788]/20 p-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#52B788]/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-[#2D6A4F]" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-[#081C15] text-sm mb-1">
                  Please accept the consent
                </h4>
                <p className="text-xs text-[#2D6A4F] leading-relaxed">
                  Check the box below to agree to our data processing terms before continuing.
                </p>
              </div>
              <button 
                onClick={() => setShowConsentNudge(false)}
                className="text-[#2D6A4F] hover:text-[#1B4332] transition-colors"
              >
                ✕
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col justify-center px-8 py-12 min-h-0">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-[#52B788] flex items-center justify-center mb-6 shadow-xl">
            <ShoppingCart className="w-10 h-10 text-[#081C15]" strokeWidth={1.5} />
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-center mb-10">
          <h1 className="text-xl text-[#081C15] font-semibold leading-tight">
            {showEmailInput ? emailSent ? "Check your inbox" : "Enter your email" : "Your weekly grocery list, automated."}
          </h1>
          
        </motion.div>

        <AnimatePresence mode="wait">
          {showEmailInput ? <motion.div key="email-input" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="space-y-4">
              {emailSent ? <div className="flex flex-col items-center space-y-6">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: "spring",
              stiffness: 200,
              damping: 15
            }} className="w-16 h-16 rounded-full bg-[#52B788]/20 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-[#52B788]" />
                  </motion.div>
                  
                  <div className="text-center space-y-2">
                    <p className="text-[#081C15] font-medium">Magic link sent!</p>
                    <p className="text-sm text-[#2D6A4F]">
                      Check your inbox at <span className="font-medium">{email}</span>
                    </p>
                  </div>

                  {/* Resend with cooldown timer */}
                  <div className="flex items-center gap-2">
                    {!canResend ? <span className="text-sm text-[#2D6A4F] flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        Resend in {cooldownSeconds}s
                      </span> : <button onClick={handleResendLink} disabled={isLoading !== null || !canResend} className="text-sm text-[#2D6A4F] hover:text-[#1B4332] transition-colors disabled:opacity-50">
                        Didn't receive the link? Resend
                      </button>}
                  </div>

                  <button onClick={handleBackFromEmail} className="text-sm text-[#2D6A4F] hover:text-[#1B4332] transition-colors mt-4">
                    Use a different email
                  </button>
                </div> : <>
                  <div className="space-y-2">
                    <Input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                if (e.key === "Enter" && isEmailValid) {
                  handleEmailSubmit();
                }
              }} className={`w-full h-14 text-base rounded-2xl border-2 bg-white/60 text-[#081C15] placeholder:text-[#2D6A4F]/50 px-4 ${email && !isEmailValid ? "border-amber-400 focus:border-amber-500" : email && isEmailValid ? "border-green-400 focus:border-green-500" : "border-[#1B4332]/20"}`} />
                    {email && !isEmailValid && <p className="text-sm text-amber-600 px-1">Please enter a valid email address</p>}
                  </div>
                  
                  <Button onClick={handleEmailSubmit} disabled={isLoading !== null || !isEmailValid} className="w-full h-14 text-base font-medium rounded-2xl bg-[#52B788] text-[#081C15] hover:bg-[#40916C] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                    {isLoading === "email" ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <Mail className="w-5 h-5 mr-3" />}
                    Send Login Link
                  </Button>
                </>}
            </motion.div> : <motion.div key="social-buttons" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }}>
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="space-y-3">
                <Button variant="outline" onClick={handleGoogleLogin} disabled={isLoading !== null} className="w-full h-14 text-base font-medium rounded-2xl border-2 border-[#1B4332]/20 bg-white/60 text-[#081C15] hover:bg-white/80 transition-all">
                  {isLoading === "google" ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>}
                  Continue with Google
                </Button>
              </motion.div>

              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.3
          }} className="mt-8 relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#1B4332]/10" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 text-sm text-[#2D6A4F]/70 bg-[#D8F3DC]">
                    or continue with email
                  </span>
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="mt-6">
                <Button variant="outline" onClick={handleEmailButtonClick} disabled={isLoading !== null} className="w-full h-14 text-base font-medium rounded-2xl border-2 border-[#1B4332]/20 bg-white/60 text-[#081C15] hover:bg-white/80 transition-all">
                  <Mail className="w-5 h-5 mr-3" />
                  Email Login
                </Button>
              </motion.div>

              {/* Consent Checkbox */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="mt-6">
                <div className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                  showConsentNudge 
                    ? "bg-[#52B788]/20 border-[#52B788] shadow-lg" 
                    : "bg-white/40 border-[#1B4332]/10"
                }`}>
                  <Checkbox
                    id="consent"
                    checked={hasConsented}
                    onCheckedChange={(checked) => {
                      setHasConsented(checked === true);
                      if (checked === true) {
                        setShowConsentNudge(false);
                      }
                    }}
                    className="mt-0.5 border-[#1B4332]/30 data-[state=checked]:bg-[#52B788] data-[state=checked]:border-[#52B788]"
                  />
                  <label 
                    htmlFor="consent" 
                    className="text-sm text-[#1B4332] leading-tight cursor-pointer"
                  >
                    By continuing, I consent to the processing of my health data for generating personalized grocery lists.
                  </label>
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="mt-6">
                <button onClick={handleGuestLogin} disabled={isLoading !== null} className="w-full text-center text-[#2D6A4F] hover:text-[#1B4332] transition-colors disabled:opacity-50">
                  {isLoading === "guest" ? <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Continuing...
                    </span> : "Continue as Guest"}
                </button>
              </motion.div>

              {/* Legal Links */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="mt-8 flex items-center justify-center gap-4 text-sm text-[#2D6A4F]">
                <Link to="/privacy" className="underline hover:opacity-80 transition-opacity">
                  Privacy Policy
                </Link>
                <span>•</span>
                <Link to="/terms" className="underline hover:opacity-80 transition-opacity">
                  Terms of Service
                </Link>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
};